import { Link } from 'react-router-dom';
import { QT_DAILY_COCKPIT_URL } from '../contextLinks';

// B1 (role-aware landing): the neutral evidence-desk orientation card that
// unmapped / new operators land on at `/` instead of being dropped into Erik's
// owner cockpit. This is the authed half of register row A1 (the anon/sign-in
// half shipped in Lane A). It orients — it does not gate or grant authority.
export function WelcomeView({ user }: { user: { firstName: string } }): JSX.Element {
  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs font-semibold uppercase tracking-wide text-cyan-700">
          Welcome{user.firstName ? `, ${user.firstName}` : ''}
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">
          Customer Evidence
        </h1>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
          This is the <span className="font-medium text-slate-800">evidence, review, exception, and
          maintenance</span> desk — not the daily action cockpit. Use it to find proof, review
          AI-extracted work, and repair evidence.
        </p>
      </header>

      <section className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
        <h2 className="text-sm font-semibold text-slate-700">Which app owns the next action?</h2>
        <ul className="mt-3 space-y-2 text-sm text-slate-700">
          <li className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium text-slate-900">Reply, call, scope, quote, close a loop</span>
            <span className="text-slate-500">→ work it in</span>
            <a
              href={QT_DAILY_COCKPIT_URL}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-slate-900 underline hover:text-slate-700"
            >
              Customer Workbench
            </a>
          </li>
          <li className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium text-slate-900">Quote, order, or invoice truth</span>
            <span className="text-slate-500">→ verify in</span>
            <span className="font-medium text-slate-900">Odoo</span>
          </li>
          <li className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium text-slate-900">Proof, review, evidence repair</span>
            <span className="text-slate-500">→ right here in</span>
            <span className="font-medium text-slate-900">Sales-QA</span>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Start here</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <Link
            to="/qa"
            className="block rounded-xl bg-white p-4 ring-1 ring-slate-200 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            <span className="block text-sm font-semibold text-slate-950">Ask evidence</span>
            <span className="mt-1 block text-sm leading-6 text-slate-600">Find proof and cite sources.</span>
          </Link>
          <Link
            to="/contacts"
            className="block rounded-xl bg-white p-4 ring-1 ring-slate-200 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            <span className="block text-sm font-semibold text-slate-950">Customer evidence</span>
            <span className="mt-1 block text-sm leading-6 text-slate-600">Look up customer context and history.</span>
          </Link>
          <Link
            to="/queues/erik"
            className="block rounded-xl bg-white p-4 ring-1 ring-slate-200 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            <span className="block text-sm font-semibold text-slate-950">Exception review</span>
            <span className="mt-1 block text-sm leading-6 text-slate-600">Owner-only decisions and misroutes.</span>
          </Link>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Your team queue (Vince handoff, Nate production, Ron follow-up) is in the sidebar under
          <span className="font-medium text-slate-600"> Team queues</span>.
        </p>
      </section>
    </div>
  );
}
